<script lang="ts" setup>
import type { ContentBookReader } from '~/models/Content/ContentBookReader'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import useContentApi from '~/api/contentApi'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'

const props = defineProps<{ locationId: number }>()

const { t } = useI18n()
const router = useRouter()
const { checkItemLicense } = useLicenseControlStore()
const { findContents } = useContentApi()
const { isAuthenticated } = storeToRefs(useAuthStore())

const { data: bookReader, isLoading } = useQuery({
  enabled: computed(() => props.locationId > 0),
  queryKey: computed(() => ['book-reader', props.locationId]),
  queryFn: () => findContents<ContentBookReader>({
    locationIdCriterion: [props.locationId],
    contentTypeCriterion: [ContentType.BookReader],
  }, 1).then(([data]) => data),
  staleTime: Infinity,
  retry: 1,
})

const { autoContentEvent } = useContentEvent()
autoContentEvent(bookReader)

const iframeUrl = computed(() => isAuthenticated.value && bookReader.value?.identifier
  ? `${import.meta.env.VITE_BOOKREADER_URL}/${bookReader.value.identifier}`
  : '')

watch(bookReader, (value) => {
  if (!value)
    return
  setTitle(value.title)
  checkItemLicense(value)
})

const closePage = (event: MessageEvent) => {
  if (!event.isTrusted)
    return
  if (event.origin !== new URL(import.meta.env.VITE_BOOKREADER_URL).origin)
    return
  if (event.data !== 'CLOSE')
    return

  router.push({ name: 'bookreader_summary', params: router.currentRoute.value.params })
}

window.addEventListener('message', closePage)
</script>

<template>
  <iframe
    v-if="iframeUrl && bookReader"
    class="size-full"
    :title="bookReader.title"
    :src="iframeUrl"
  />

  <div
    v-else-if="isLoading"
    class="h-full p-6"
  >
    <AuSkeletonWrapper
      class="grid h-full place-content-center gap-2"
      :text="t('bookreader.loading')"
    >
      <AuSkeleton
        width="30ch"
        height="2rem"
      />
      <AuSkeleton width="50ch" />
      <AuSkeleton width="40ch" />
      <AuSkeleton width="45ch" />
      <AuSkeleton width="43ch" />
      <AuSkeleton width="52ch" />
      <AuSkeleton width="35ch" />
    </AuSkeletonWrapper>
  </div>

  <div
    v-else-if="isAuthenticated"
    class="grid h-full place-content-center p-6"
  >
    {{ t('bookreader.error') }}
  </div>
</template>
